import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Heart, ShoppingCart, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import AppContext from "../../../context/AppContext";
import "../../../assests/css/customer/ProductCard/ProductCard.css";

const ToastPortal = ({ toasts }) =>
  ReactDOM.createPortal(
    <div className="pc-toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`pc-toast pc-toast--${t.variant}`}>
          <span className="pc-toast__icon-wrap">
            {t.variant === "remove" ? (
              <X size={14} strokeWidth={3} />
            ) : (
              <Check size={14} strokeWidth={3} />
            )}
          </span>

          <span className="pc-toast__msg">{t.msg}</span>
        </div>
      ))}
    </div>,
    document.body
  );

// Customization designs wali subcategory
const CUSTOMIZATION_SUBCATEGORY_ID = 18;

// ---------------------------------------------------------
// FIX: customer object /profile API se ASYNC load hota hai.
// Fresh page load par token turant mil jata hai lekin customer
// object thodi der baad aata hai. Is helper se hum localStorage
// se seedha customer id nikal lete hain agar context ka customer
// abhi load nahi hua — isse add-to-cart / wishlist silently fail
// nahi hote aur bar bar login popup bhi nahi aata.
// ---------------------------------------------------------
const getFallbackCustomerId = () => {
  try {
    const c = localStorage.getItem("loggedInCustomer");
    const parsed = c && c !== "undefined" && c !== "null" ? JSON.parse(c) : null;
    return parsed?.id || null;
  } catch {
    return null;
  }
};

const ProductCard = ({
  product,
  wishlist = [],
  toggleWishlist,
  cart = [],
  toggleCart,
  onCardClick,
  type,
  tryOnButton,
}) => {
  const navigate = useNavigate();

  const {
    baseUrl,
    baseUrlForFiles,
    setCartCount,
    setWishlistCount,
    setUploadImage,
    setCustomizeImage,
    customer,
    requireLogin,
  } = useContext(AppContext);

  const [activeIdx, setActiveIdx] = useState(0);
  const [toasts, setToasts] = useState([]);

  const [localWishlisted, setLocalWishlisted] = useState(
    wishlist.includes(product.id)
  );

  // customer.id ready na ho to localStorage se fallback le lo
  const customerId = customer?.id || getFallbackCustomerId();

  // ---------------------------------------------------------
  // Wishlist sync
  // ---------------------------------------------------------
  useEffect(() => {
    setLocalWishlisted(wishlist.includes(product.id));
  }, [wishlist, product.id]);

  // ---------------------------------------------------------
  // Cart sync
  // ---------------------------------------------------------
  const existingCartItem = cart.find(
    (item) => Number(item.Mlproduct_id) === Number(product.id)
  );

  const [localInCart, setLocalInCart] = useState(
    !!existingCartItem
  );

  const [localCartId, setLocalCartId] = useState(
    existingCartItem?.id || null
  );

  // ---------------------------------------------------------
  // PRODUCT IMAGES
  // ---------------------------------------------------------

  const getImageUrl = (imageValue) => {
    if (!imageValue) return null;

    // Agar already complete URL hai
    if (
      typeof imageValue === "string" &&
      (imageValue.startsWith("http://") ||
        imageValue.startsWith("https://"))
    ) {
      return imageValue;
    }

    // Agar object hai aur image_path hai
    if (
      typeof imageValue === "object" &&
      imageValue?.image_path
    ) {
      const path = imageValue.image_path;

      if (
        path.startsWith("http://") ||
        path.startsWith("https://")
      ) {
        return path;
      }

      return `${baseUrlForFiles}/${path}`;
    }

    // Agar string path hai
    if (typeof imageValue === "string") {
      return `${baseUrlForFiles}/${imageValue}`;
    }

    return null;
  };

  // ---------------------------------------------------------
  // Product card ki images
  // ---------------------------------------------------------

  const images = Array.isArray(product.images)
    ? product.images
        .map((img) => getImageUrl(img))
        .filter(Boolean)
    : product.image
    ? [getImageUrl(product.image)].filter(Boolean)
    : [];

  // ---------------------------------------------------------
  // IMPORTANT:
  // Ye wahi ORIGINAL product image hai jo
  // Customization page par bhejni hai.
  // ---------------------------------------------------------

  const getProductImage = () => {
    // 1. Product images array
    if (
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const firstImage = product.images[0];

      const imageUrl = getImageUrl(firstImage);

      if (imageUrl) {
        return imageUrl;
      }
    }

    // 2. Product.image fallback
    if (product.image) {
      const imageUrl = getImageUrl(product.image);

      if (imageUrl) {
        return imageUrl;
      }
    }

    // 3. Kuch APIs mein image_path directly aa sakta hai
    if (product.image_path) {
      const imageUrl = getImageUrl(product.image_path);

      if (imageUrl) {
        return imageUrl;
      }
    }

    return null;
  };

  // ---------------------------------------------------------
  // Price
  // ---------------------------------------------------------

  const basePriceNum = parseFloat(
    product.base_price ?? product.price ?? 0
  );

  const discountPriceNum = parseFloat(
    product.discount_price ?? 0
  );

  const isOnSale =
    (product.enable_sale === true ||
      product.enable_sale === 1 ||
      product.enable_sale === "1") &&
    discountPriceNum > 0 &&
    basePriceNum > 0 &&
    discountPriceNum < basePriceNum;

  const salePercentOff = isOnSale
    ? Math.round(
        ((basePriceNum - discountPriceNum) /
          basePriceNum) *
          100
      )
    : 0;

  // ---------------------------------------------------------
  // Toast
  // ---------------------------------------------------------

  const showToast = (msg, variant) => {
    const id = Date.now();

    setToasts((prev) => [
      ...prev,
      {
        id,
        msg,
        variant,
      },
    ]);

    setTimeout(() => {
      setToasts((prev) =>
        prev.filter((t) => t.id !== id)
      );
    }, 2500);
  };

  // ---------------------------------------------------------
  // ADD TO CART
  // ---------------------------------------------------------

  const addToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // LOGIN CHECK — login ke bina add to cart nahi hoga
    // AppContext ka centralized requireLogin use ho raha hai
    if (!requireLogin()) return;

    // customer object abhi load ho raha ho to bhi localStorage
    // fallback se id mil jayegi (getFallbackCustomerId upar defined)
    const currentCustomerId = customerId;

    if (!currentCustomerId) {
      Swal.fire({
        icon: "info",
        title: "Please wait",
        text: "Loading your profile, try again in a moment.",
        timer: 1800,
        showConfirmButton: false,
      });
      return;
    }

    setLocalInCart(true);

    try {
      const res = await axios.post(
        `${baseUrl}/customer/cart/addtocart`,
        {
          title: product.name,
          price: product.price,

          quantity: 1,

          image_path:
            product.images?.[0]?.image_path
              ? `${baseUrlForFiles}/${product.images[0].image_path}`
              : getProductImage(),

          color: product.color || "Default",

          size: product.size || "M",

          Mlproduct_id: product.id,

          Mlcustomer_id: currentCustomerId,
        }
      );

      if (res.data.success) {
        setLocalCartId(res.data.cart_id);

        if (toggleCart) {
          toggleCart(
            product.id,
            res.data.cart_id
          );
        }

        setCartCount((prev) => prev + 1);

        showToast(
          "Added To Cart",
          "cart"
        );
      } else {
        setLocalInCart(false);
      }
    } catch (err) {
      console.log(err);

      setLocalInCart(false);

      Swal.fire(
        "Error",
        "Add to cart failed",
        "error"
      );
    }
  };

  // ---------------------------------------------------------
  // DELETE FROM CART
  // ---------------------------------------------------------

  const deleteFromCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!requireLogin()) return;

    const cartItemFromProp = cart.find(
      (item) =>
        Number(item.Mlproduct_id) ===
        Number(product.id)
    );

    const cartIdToDelete =
      cartItemFromProp?.id ||
      localCartId;

    if (!cartIdToDelete) {
      return showToast(
        "Item not found in cart",
        "remove"
      );
    }

    setLocalInCart(false);

    try {
      const res = await axios.post(
        `${baseUrl}/customer/cart/deleteCart`,
        {
          cart_id: cartIdToDelete,
        }
      );

      if (res.data.success) {
        setLocalCartId(null);

        if (toggleCart) {
          toggleCart(
            product.id,
            cartIdToDelete
          );
        }

        setCartCount((prev) =>
          Math.max(prev - 1, 0)
        );

        showToast(
          "Removed From Cart",
          "remove"
        );
      } else {
        setLocalInCart(true);
      }
    } catch (err) {
      console.log(err);

      setLocalInCart(true);

      Swal.fire(
        "Error",
        "Delete failed",
        "error"
      );
    }
  };

  // ---------------------------------------------------------
  // WISHLIST
  // ---------------------------------------------------------

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // LOGIN CHECK — login ke bina wishlist nahi hogi
    if (!requireLogin()) return;

    const currentCustomerId = customerId;

    if (!currentCustomerId) {
      Swal.fire({
        icon: "info",
        title: "Please wait",
        text: "Loading your profile, try again in a moment.",
        timer: 1800,
        showConfirmButton: false,
      });
      return;
    }

    if (localWishlisted) {
      setLocalWishlisted(false);

      try {
        await toggleWishlist(product.id);

        setWishlistCount((prev) =>
          Math.max(prev - 1, 0)
        );

        showToast(
          "Removed Wishlist",
          "remove"
        );
      } catch (err) {
        setLocalWishlisted(true);
      }
    } else {
      setLocalWishlisted(true);

      try {
        const res = await axios.post(
          `${baseUrl}/customer/wishlists/addtowishlist`,
          {
            mlcustomer_id: currentCustomerId,
            mlproduct_id: product.id,
          }
        );

        if (res.data.success) {
          toggleWishlist(product.id);

          setWishlistCount(
            (prev) => prev + 1
          );

          showToast(
            "Added Wishlist",
            "wishlist"
          );
        } else {
          setLocalWishlisted(false);
        }
      } catch (err) {
        setLocalWishlisted(false);

        Swal.fire(
          "Error",
          "Add wishlist failed",
          "error"
        );
      }
    }
  };

  // ---------------------------------------------------------
  // CARD CLICK
  // ---------------------------------------------------------

  const handleCardClick = () => {
    if (onCardClick) {
      return onCardClick();
    }

    navigate(
      `/product-detail/${product.id}`
    );
  };

  // ---------------------------------------------------------
  // TRY ON / CUSTOMIZE
  // ---------------------------------------------------------

  const handleFeatureBtn = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();

    // =====================================================
    // TRY ON
    // =====================================================

    if (mode === "tryon") {
      setUploadImage(product);

      navigate("/upload");

      return;
    }

    // =====================================================
    // CUSTOMIZE
    // =====================================================

    if (mode === "customize") {
      // Context mein complete product save
      setCustomizeImage(product);

      // ---------------------------------------------------
      // ORIGINAL PRODUCT IMAGE
      // ---------------------------------------------------

      const productImage =
        getProductImage();

      // Debug ke liye
      console.log(
        "CUSTOMIZATION PRODUCT:",
        product
      );

      console.log(
        "CUSTOMIZATION PRODUCT IMAGE:",
        productImage
      );

      // ---------------------------------------------------
      // CATEGORY ID
      // ---------------------------------------------------

      const categoryId = Number(
        product.mlcategory_id ??
          product.category_id ??
          product.category?.id ??
          product.mlcategory?.id ??
          0
      );

      // ---------------------------------------------------
      // SUBCATEGORY ID
      // ---------------------------------------------------

      const subcategoryId = Number(
        product.mlsubcategory_id ??
          product.subcategory_id ??
          product.subcategory?.id ??
          0
      );

      console.log(
        "CUSTOMIZATION CATEGORY ID:",
        categoryId
      );

      console.log(
        "CUSTOMIZATION SUBCATEGORY ID:",
        subcategoryId
      );

      // ---------------------------------------------------
      // NAVIGATE TO CUSTOMIZATION
      // ---------------------------------------------------

      navigate("/customization", {
        state: {
          // Simple customization flag
          isSimpleCustomization:
            isCustomizationProduct,

          // ORIGINAL DRESS IMAGE
          productImage:
            productImage,

          // Product information
          productName:
            product.name,

          productId:
            product.id,

          // Category
          categoryId:
            categoryId,

          // Subcategory
          subcategoryId:
            subcategoryId,

          // Full product object
          product:
            product,
        },
      });

      return;
    }
  };

  // ---------------------------------------------------------
  // BUY
  // ---------------------------------------------------------

  const handleBuyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    navigate(
      `/product-detail/${product.id}`
    );
  };

  // ---------------------------------------------------------
  // PRODUCT TYPE
  // ---------------------------------------------------------

  const isTryOn =
    type === "stitched";

  const isAccessory =
    type === "accessories";

  // ---------------------------------------------------------
  // CUSTOMIZATION PRODUCT
  // ---------------------------------------------------------

  const isCustomizationProduct =
    Number(product.mlsubcategory_id) ===
      CUSTOMIZATION_SUBCATEGORY_ID ||
    Number(product.subcategory_id) ===
      CUSTOMIZATION_SUBCATEGORY_ID;

  // ---------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------

  return (
    <>
      <ToastPortal toasts={toasts} />

      <div
        className="pc-card"
        onMouseEnter={() =>
          images.length > 1 &&
          setActiveIdx(1)
        }
        onMouseLeave={() =>
          setActiveIdx(0)
        }
        onClick={handleCardClick}
      >
        {/* =================================================
            IMAGE
        ================================================= */}

        <div className="pc-image-container">
          {isOnSale && (
            <span className="pc-sale-badge">
              -{salePercentOff}%
            </span>
          )}

          <div className="pc-image-wrapper">
            {images.length ? (
              images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={product.name}
                  className={`pc-image ${
                    i === activeIdx
                      ? "pc-image--active"
                      : ""
                  }`}
                />
              ))
            ) : (
              <div className="pc-no-image">
                No Image
              </div>
            )}
          </div>

          {/* =================================================
              ACTION BUTTONS
          ================================================= */}

          <div className="pc-actions">
            <button
              className={`pc-icon-btn pc-wishlist-btn ${
                localWishlisted
                  ? "pc-icon-btn--active"
                  : ""
              }`}
              onClick={handleWishlist}
            >
              <Heart
                size={16}
                fill={
                  localWishlisted
                    ? "#fff"
                    : "none"
                }
                color={
                  localWishlisted
                    ? "#fff"
                    : "#e63946"
                }
              />
            </button>

            <button
              className={`pc-icon-btn pc-cart-btn ${
                localInCart
                  ? "pc-icon-btn--active"
                  : ""
              }`}
              onClick={(e) =>
                localInCart
                  ? deleteFromCart(e)
                  : addToCart(e)
              }
            >
              <ShoppingCart
                size={16}
                fill={
                  localInCart
                    ? "#fff"
                    : "none"
                }
                color={
                  localInCart
                    ? "#fff"
                    : "#AD8B73"
                }
              />
            </button>
          </div>
        </div>

        {/* =================================================
            PRODUCT INFO
        ================================================= */}

        <div className="pc-info">
          <h3 className="pc-name">
            {product.name}
          </h3>

          <div className="pc-footer">
            <span className="pc-price">
              {isOnSale ? (
                <>
                  <span className="pc-price--original">
                    Rs.{" "}
                    {basePriceNum.toLocaleString()}
                  </span>{" "}

                  <span className="pc-price--sale">
                    Rs.{" "}
                    {discountPriceNum.toLocaleString()}
                  </span>
                </>
              ) : (
                <>
                  Rs.{" "}
                  {(
                    product.price ??
                    basePriceNum
                  ).toLocaleString()}
                </>
              )}
            </span>

            {/* =================================================
                ACCESSORY = BUY
            ================================================= */}

            {isAccessory ? (
              <button
                className="pc-feature-btn pc-feature-btn--buy"
                onClick={handleBuyClick}
              >
                Buy
              </button>
            ) : (

              /* =================================================
                 STITCHED = TRY ON
                 UNSTITCHED = CUSTOMIZE
              ================================================= */

              <button
                className={`pc-feature-btn ${
                  isTryOn
                    ? "pc-feature-btn--tryon"
                    : "pc-feature-btn--customize"
                }`}
                onClick={(e) =>
                  handleFeatureBtn(
                    e,
                    isTryOn
                      ? "tryon"
                      : "customize"
                  )
                }
              >
                {isTryOn
                  ? "Try On"
                  : "Customize"}
              </button>
            )}
          </div>

          {tryOnButton}
        </div>
      </div>
    </>
  );
};

export default ProductCard;